/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useHistory } from 'react-router-dom';
import { Fragment, useContext, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import Table, { TableProps } from 'antd/lib/table';
import ProjectSrc from '~src/assets/nav-icons/project.png';

// local imports
import { useStyles } from '~src/hooks/useStyles';
import { useProjectId } from '~src/hooks/useProjectId';
import {
  dbProjectToTableEntry,
  sortByNumberProp,
  TableEntry,
} from './FileTable.utils';
import IconButton from '~components/_common/IconButton/IconButton';
import { styleFn } from './FileTable.style';
import { FileTableProps } from './FileTable';
import { __DEV__ } from '~src/config';
import FileVersionsContext from '~src/contexts/FileVersionsContext';
import { formatDate } from '~src/utils/formatDate';
import { NavIcon } from '~components/_common/NavIcon/NavIcon';
import { useProjectList } from '~src/hooks/api-hooks/useProjectList';
import { SearchContext } from '~src/contexts/SearchContext';
import { filterByQuery } from '~src/utils/searchFilters';

const sortByUpdatedAt = sortByNumberProp('updatedAt');

export const ProjectWebTable: React.FC<FileTableProps> = ({
  selectedKeys,
  highlightedRow,
  onSelect,
  setSelectedItems,
  handleRow,
  ...tableProps
}) => {
  const styles = useStyles(styleFn);
  const intl = useIntl();
  const projectId = useProjectId();
  const history = useHistory();
  const { fileVersionsContext } = useContext(FileVersionsContext);
  const searchContext = useContext(SearchContext);
  const [filteredTendersList, setFilteredTendersList] = useState<TableEntry[]>(
    []
  );

  const projectListQuery = useProjectList({
    // onError: () => history.replace(`/projects/${projectId}`),
    refetchOnMount: false,
  });

  const filesSource = useMemo(
    () => (projectListQuery.data || []).map(dbProjectToTableEntry),
    [projectListQuery.data]
  );

  const filteredFiles = Array.isArray(filesSource) ? [...filesSource] : [];

  useEffect(() => {
    let filteredProjects = filteredFiles;
    setFilteredTendersList(filteredProjects);
    if (searchContext?.query) {
      filteredProjects = filteredProjects.filter((item) => {
        const matchingName = filterByQuery(item?.name, searchContext?.query);
        return matchingName;
      });
    }
    return setFilteredTendersList(filteredProjects);
  }, [searchContext?.query]);

  // console.log(`filteredFiles`, filteredFiles, projectListQuery);

  const columns = useMemo<TableProps<TableEntry>['columns']>(
    () => [
      {
        title: intl.formatMessage({ id: 'type' }),
        dataIndex: 'type',
        width: 50,
        align: 'center',
        render: (type, record) => {
          return {
            children: (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}>
                <div style={{ marginLeft: '10px', width: 30, height: 30 }}>
                  <NavIcon icon={ProjectSrc} />
                </div>
              </div>
            ),
          };
        },
      },
      {
        title: intl.formatMessage({ id: 'name' }),
        dataIndex: 'name',
        width: 250,
        sorter: (a, b) => a.name.localeCompare(b.name),
        render: (name, record) => (
          <div style={{ marginLeft: '10px' }}>
            {name}
            {record.favourite && (
              <IconButton icon={['fas', 'star']} color="yellow" backgroundOff />
            )}
          </div>
        ),
      },
      {
        title: intl.formatMessage({ id: 'description' }),
        dataIndex: 'description',
        sorter: (a, b) =>
          (a.description || '').localeCompare(b.description || ''),
        // width: 200,
        ellipsis: true,
      },
      {
        title: intl.formatMessage({ id: 'start-date' }),
        dataIndex: 'start_date',
        sorter: sortByUpdatedAt,
        width: 200,
        align: 'center',
        render: (updatedAt) => formatDate(updatedAt, 'short'),
      },
    ],
    [fileVersionsContext, intl, styles.icons, styles.small]
  );

  return (
    <Table<TableEntry>
      showSorterTooltip={false}
      css={[
        styles.table,
        { marginTop: '20px' },
        tableProps.scroll?.y && {
          '.ant-table-body': {
            height: tableProps.scroll.y,
          },
        },
      ]}
      className="main-content-window"
      pagination={false}
      bordered
      columns={columns}
      dataSource={filteredTendersList}
      loading={projectListQuery.isLoading || projectListQuery.isIdle}
      onRow={(record, index) => {
        return {
          onClick: (event) => {
            history.push({
              pathname: `/my-projects/${record.id}`,
            });
          },
        };
      }}
      {...tableProps}
    />
  );
};
